import { debounce, parseInteger } from './scripts/utils.js';
import { bindSliderPair } from './scripts/slider.js';
import { bindTabs } from './scripts/tabs.js';
import { bindBillingToggle } from './scripts/billing-toggle.js';
import { createPricingRequester } from './scripts/pricing-api.js';
import { renderPricing, renderPricingError } from './scripts/pricing-render.js';

const i18n = window.pricingCalculatorData?.i18n || {};

const PLAN_LABELS = {
	core: 'Core',
	ultimate: 'Ultimate',
	extended: 'Extended',
};

const VALID_TAB_PLAN_MODIFIERS = new Set( Object.keys( PLAN_LABELS ) );

const PLAN_ASSET_INDEX = {
	core: 2,
	ultimate: 4,
	extended: 3,
};

/**
 * Resolves tab tier label for given mode key.
 *
 * @param {HTMLElement} block Calculator block root.
 * @param {string} modeKey Tab mode key.
 * @return {string} Tier label.
 */
const getTabTierLabel = ( block, modeKey ) => {

	const panel = block.querySelector( `.b-pricing-calculator__mode[data-mode="${ modeKey }"]` );
	if ( ! panel ) {

		return '';

	}

	const explicitTier = `${ panel.dataset.planTier || '' }`.trim();
	if ( explicitTier ) {

		return explicitTier;

	}

	return `${ panel.dataset.plans || '' }`
		.split( ',' )
		.map( ( value ) => value.trim() )
		.filter( Boolean )
		.join( '+' );

};

/**
 * Tracks Amplitude event when calculator tab changes.
 *
 * @param {HTMLElement} block Calculator block root.
 * @param {string} fromMode Previous tab mode key.
 * @param {string} toMode Next tab mode key.
 * @return {void}
 */
const trackAmplitudeTabSwitch = ( block, fromMode, toMode ) => {

	if ( ! block || fromMode === toMode ) {

		return;

	}

	if ( block.dataset.amplitudeTabSwitchEnabled !== 'true' ) {

		return;

	}

	const eventName = block.dataset.amplitudeTabSwitchEventName?.trim();
	if ( ! eventName ) {

		return;

	}

	const track = window?.amplitude?.track;
	if ( typeof track !== 'function' ) {

		return;

	}

	const fromTier = getTabTierLabel( block, fromMode );
	const toTier = getTabTierLabel( block, toMode );

	if ( ! fromTier || ! toTier ) {

		return;

	}

	track( eventName, {
		'from_tier': fromTier,
		'to_tier': toTier,
	} );

};

/**
 * Tracks Amplitude event when billing cycle changes.
 *
 * @param {HTMLElement} block Calculator block root.
 * @param {string} fromCycle Previous cycle key.
 * @param {string} toCycle Next cycle key.
 * @param {string} modeKey Active tab mode key.
 * @return {void}
 */
const trackAmplitudeBillingCycleToggle = ( block, fromCycle, toCycle, modeKey ) => {

	if ( ! block || fromCycle === toCycle ) {

		return;

	}

	if ( block.dataset.amplitudeBillingCycleToggleEnabled !== 'true' ) {

		return;

	}

	const eventName = block.dataset.amplitudeBillingCycleToggleEventName?.trim();
	if ( ! eventName ) {

		return;

	}

	const track = window?.amplitude?.track;
	if ( typeof track !== 'function' ) {

		return;

	}

	const planTier = getTabTierLabel( block, modeKey );
	if ( ! planTier ) {

		return;

	}

	track( eventName, {
		'from_cycle': fromCycle,
		'to_cycle': toCycle,
		'plan_tier': planTier,
	} );

};

/**
 * Tracks Amplitude event when seats change in active tab.
 *
 * @param {HTMLElement} block Calculator block root.
 * @param {string} modeKey Active tab mode key.
 * @param {{ coreSeats: number, ultimateSeats: number, extendedSeats: number }} seats Seats map.
 * @return {void}
 */
const trackAmplitudeSeatsAdjusted = ( block, modeKey, seats ) => {

	if ( ! block ) {

		return;

	}

	if ( block.dataset.amplitudeSeatsAdjustedEnabled !== 'true' ) {

		return;

	}

	const eventName = block.dataset.amplitudeSeatsAdjustedEventName?.trim();
	if ( ! eventName ) {

		return;

	}

	const track = window?.amplitude?.track;
	if ( typeof track !== 'function' ) {

		return;

	}

	const planTier = getTabTierLabel( block, modeKey );
	if ( ! planTier ) {

		return;

	}

	track( eventName, {
		'plan_tier': planTier,
		'core_seats': seats.coreSeats,
		'ultimate_seats': seats.ultimateSeats,
	} );

};

/**
 * Tracks Amplitude event when visible CTA zone changes.
 *
 * @param {HTMLElement} block Calculator block root.
 * @param {string} zone Active CTA zone.
 * @param {string} modeKey Active tab mode key.
 * @param {number} subtotal Current subtotal.
 * @param {string} billingTerm Current billing term.
 * @param {{ coreSeats: number, ultimateSeats: number, extendedSeats: number }} seats Seats map.
 * @return {void}
 */
const trackAmplitudeCtaZoneShown = ( block, zone, modeKey, subtotal, billingTerm, seats ) => {

	if ( ! block || ! zone ) {

		return;

	}

	if ( block.dataset.amplitudeCtaZoneShownEnabled !== 'true' ) {

		return;

	}

	const eventName = block.dataset.amplitudeCtaZoneShownEventName?.trim();
	if ( ! eventName ) {

		return;

	}

	const track = window?.amplitude?.track;
	if ( typeof track !== 'function' ) {

		return;

	}

	const planTier = getTabTierLabel( block, modeKey );
	if ( ! planTier ) {

		return;

	}

	track( eventName, {
		zone,
		'plan_tier': planTier,
		value: Math.max( 0, Math.round( Number( subtotal ) || 0 ) ),
		'billing_term': billingTerm,
		'core_seats': seats.coreSeats,
		'ultimate_seats': seats.ultimateSeats,
	} );

};

/**
 * Builds package node for tab header.
 *
 * @param {string} planKey Plan key.
 * @param {string} themeUrl Theme URL.
 * @param {boolean} withLogo Whether icon should be rendered.
 * @return {HTMLElement} Package node.
 */
const createTabPackageNode = ( planKey, themeUrl, withLogo ) => {

	const packageNode = document.createElement( 'span' );
	packageNode.className = `b-pricing-calculator__tab-package b-pricing-calculator__tab-package--${ planKey }`;

	if ( withLogo && themeUrl && PLAN_ASSET_INDEX[ planKey ] ) {

		const index = PLAN_ASSET_INDEX[ planKey ];
		const iconDefault = document.createElement( 'img' );
		iconDefault.className = 'b-pricing-calculator__tab-package-icon b-pricing-calculator__tab-package-icon--default';
		iconDefault.src = `${ themeUrl }/images/price-plans/tabs/plan-${ index }.svg`;
		iconDefault.alt = '';
		iconDefault.setAttribute( 'aria-hidden', 'true' );
		packageNode.appendChild( iconDefault );

		const iconActive = document.createElement( 'img' );
		iconActive.className = 'b-pricing-calculator__tab-package-icon b-pricing-calculator__tab-package-icon--active';
		iconActive.src = `${ themeUrl }/images/price-plans/tabs/plan-${ index }-light.svg`;
		iconActive.alt = '';
		iconActive.setAttribute( 'aria-hidden', 'true' );
		packageNode.appendChild( iconActive );

	}

	const nameNode = document.createElement( 'span' );
	nameNode.className = 'b-pricing-calculator__tab-package-name';
	nameNode.textContent = PLAN_LABELS[ planKey ] || planKey;
	packageNode.appendChild( nameNode );

	const badgeNode = document.createElement( 'span' );
	badgeNode.className = 'b-pricing-calculator__tab-package-pro';
	badgeNode.textContent = 'PRO';
	packageNode.appendChild( badgeNode );

	return packageNode;

};

/**
 * Builds tab buttons from saved tab panels.
 *
 * @param {HTMLElement} block Pricing calculator block root.
 * @return {{ tabs: NodeListOf<HTMLButtonElement>, panels: NodeListOf<HTMLElement> }} Tabs and panels collections.
 */
const buildTabsFromPanels = ( block ) => {

	const tabsContainer = block.querySelector( '.b-pricing-calculator__plan-tabs' );
	const panels = block.querySelectorAll( '.b-pricing-calculator__mode' );
	const themeUrl = block.dataset.themeUrl || '';

	if ( ! tabsContainer ) {

		return {
			tabs: /** @type {NodeListOf<HTMLButtonElement>} */ ( [] ),
			panels,
		};

	}

	tabsContainer.innerHTML = '';

	panels.forEach( ( panel, index ) => {

		const modeKey = `tab-${ index }`;
		const panelId = panel.id || `pc-mode-${ index + 1 }`;
		const tabId = `pc-tab-${ index + 1 }`;
		const tabDescription = panel.dataset.tabDescription || '';
		const parsedPlans = String( panel.dataset.plans || '' )
			.split( ',' )
			.map( ( value ) => value.trim().toLowerCase() )
			.filter( Boolean );
		const plans = parsedPlans.length > 0 ? parsedPlans : [ 'core' ];
		const isActive = index === 0;
		const isMultiPlan = plans.length > 1;
		const singlePlanModifier = ! isMultiPlan && VALID_TAB_PLAN_MODIFIERS.has( plans[ 0 ] )
			? ` b-pricing-calculator__plan-tab--${ plans[ 0 ] }`
			: '';

		panel.id = panelId;
		panel.dataset.mode = modeKey;
		panel.setAttribute( 'aria-labelledby', tabId );
		panel.toggleAttribute( 'hidden', ! isActive );
		panel.classList.toggle( 'b-pricing-calculator__mode--multi', isMultiPlan );

		const button = document.createElement( 'button' );
		button.className = `b-pricing-calculator__plan-tab${ isActive ? ' b-pricing-calculator__plan-tab--active' : '' }${ isMultiPlan ? ' b-pricing-calculator__plan-tab--multi' : '' }${ singlePlanModifier }`;
		button.setAttribute( 'role', 'tab' );
		button.dataset.plan = modeKey;
		button.id = tabId;
		button.setAttribute( 'aria-controls', panelId );
		button.setAttribute( 'aria-selected', String( isActive ) );

		if ( ! isActive ) {

			button.setAttribute( 'tabindex', '-1' );

		}

		const heading = document.createElement( 'span' );
		heading.className = 'b-pricing-calculator__tab-heading';

		plans.forEach( ( planKey, planIndex ) => {

			if ( planIndex > 0 ) {

				const plusNode = document.createElement( 'span' );
				plusNode.className = 'b-pricing-calculator__tab-plus';
				plusNode.textContent = '+';
				heading.appendChild( plusNode );

			}

			heading.appendChild( createTabPackageNode( planKey, themeUrl, ! isMultiPlan ) );

		} );

		button.appendChild( heading );

		if ( tabDescription ) {

			const descriptionNode = document.createElement( 'span' );
			descriptionNode.className = 'b-pricing-calculator__tab-description';
			descriptionNode.textContent = tabDescription;
			button.appendChild( descriptionNode );

		}

		tabsContainer.appendChild( button );

	} );

	return {
		tabs: tabsContainer.querySelectorAll( '.b-pricing-calculator__plan-tab[data-plan]' ),
		panels,
	};

};

/**
 * Initialises one pricing calculator block instance.
 *
 * @param {HTMLElement} block Pricing calculator block root.
 * @return {void}
 */
const initPricingCalculator = ( block ) => {

	const apiUrl = block.dataset.apiUrl || '';
	const templates = {
		savingsAnnual: block.dataset.savingsAnnualTemplate || '',
		savingsMonthly: block.dataset.savingsMonthlyTemplate || '',
	};

	if ( ! apiUrl ) {

		return;

	}

	const { tabs, panels } = buildTabsFromPanels( block );

	const controls = {
		billingButtons: block.querySelectorAll( '[data-term]' ),
		tabs,
		panels,
		planGroups: block.querySelectorAll( '.b-pricing-calculator__plan-control' ),
	};

	const loadingOverlay = block.querySelector( '.b-pricing-calculator__loading-overlay' );
	const activeTermButton = block.querySelector( '.b-pricing-calculator__toggle-btn--active[data-term]' );
	const activePlanTab = block.querySelector( '.b-pricing-calculator__plan-tab--active[data-plan]' );

	const state = {
		term: activeTermButton?.dataset.term || 'annual',
		planMode: activePlanTab?.dataset.plan || 'tab-0',
		activeCtaZone: '',
	};

	const cacheByMode = new Map();
	const requestersByMode = new Map();

	/**
	 * Returns panel by mode key.
	 *
	 * @param {string} modeKey Mode key.
	 * @return {HTMLElement|null} Tab panel.
	 */
	const getPanelByMode = ( modeKey ) =>
		block.querySelector( `.b-pricing-calculator__mode[data-mode="${ modeKey }"]` );

	/**
	 * Shows loading overlay.
	 *
	 * @return {void}
	 */
	const showLoading = () => {

		if ( ! loadingOverlay ) {

			return;

		}

		loadingOverlay.removeAttribute( 'hidden' );
		loadingOverlay.setAttribute( 'aria-hidden', 'false' );

	};

	/**
	 * Hides loading overlay.
	 *
	 * @return {void}
	 */
	const hideLoading = () => {

		if ( ! loadingOverlay ) {

			return;

		}

		loadingOverlay.setAttribute( 'hidden', '' );
		loadingOverlay.setAttribute( 'aria-hidden', 'true' );

	};

	/**
	 * Returns render elements scoped to one panel.
	 *
	 * @param {HTMLElement|null} panel Panel root.
	 * @return {Object} Elements used by pricing renderer.
	 */
	const getPanelRenderElements = ( panel ) => ( {
		priceDisplay: panel?.querySelector( '.b-pricing-calculator__price-display' ) || null,
		priceAmount: panel?.querySelector( '.b-pricing-calculator__price-amount' ) || null,
		pricePeriod: panel?.querySelector( '.b-pricing-calculator__price-period' ) || null,
		priceSavings: panel?.querySelector( '.b-pricing-calculator__price-savings' ) || null,
		errorMessage: panel?.querySelector( '.b-pricing-calculator__error' ) || null,
		breakdown: panel?.querySelector( '.b-pricing-calculator__breakdown' ) || null,
		panelPlanTier: panel?.dataset.planTier || '',
		panelPlans: panel?.dataset.plans || '',
		planControls: panel ? panel.querySelectorAll( '.b-pricing-calculator__plan-control[data-plan-key]' ) : [],
		ctaBlocks: panel ? panel.querySelectorAll( '.b-pricing-calculator__cta-area' ) : [],
	} );

	/**
	 * Returns seats for given panel.
	 *
	 * @param {HTMLElement|null} panel Panel root.
	 * @return {{ coreSeats: number, ultimateSeats: number, extendedSeats: number }} Seats.
	 */
	const getSeatsForPanel = ( panel ) => {

		const seatMap = {
			core: 0,
			ultimate: 0,
			extended: 0,
		};

		if ( ! panel ) {

			return {
				coreSeats: 0,
				ultimateSeats: 0,
				extendedSeats: 0,
			};

		}

		panel.querySelectorAll( '.b-pricing-calculator__plan-control[data-plan-key]' ).forEach( ( group ) => {

			const planKey = String( group.getAttribute( 'data-plan-key' ) || '' ).toLowerCase();
			const numberInput = group.querySelector( '[data-role="seat-input"]' );
			const seatsValue = parseInteger( numberInput?.value, 0 );

			if ( planKey in seatMap ) {

				seatMap[ planKey ] = seatsValue;

			}

		} );

		return {
			coreSeats: seatMap.core,
			ultimateSeats: seatMap.ultimate,
			extendedSeats: seatMap.extended,
		};

	};

	/**
	 * Builds cache signature for seats.
	 *
	 * @param {{ coreSeats: number, ultimateSeats: number, extendedSeats: number }} seats Seats map.
	 * @return {string} Signature key.
	 */
	const getSeatSignature = ( seats ) => `${ seats.coreSeats }|${ seats.ultimateSeats }|${ seats.extendedSeats }`;

	/**
	 * Returns cached pricing for panel mode and seat signature.
	 *
	 * @param {string} modeKey Mode key.
	 * @param {string} seatSignature Signature.
	 * @return {{ annualData: Object, monthlyData: Object }|null} Cached data.
	 */
	const getCachedPricing = ( modeKey, seatSignature ) => {

		const cacheItem = cacheByMode.get( modeKey );

		if ( ! cacheItem || cacheItem.seatSignature !== seatSignature ) {

			return null;

		}

		return {
			annualData: cacheItem.annualData,
			monthlyData: cacheItem.monthlyData,
		};

	};

	/**
	 * Renders one panel from provided pricing pair.
	 *
	 * @param {HTMLElement|null} panel Panel root.
	 * @param {{ annualData: Object, monthlyData: Object }} data Pricing data.
	 * @return {{ subtotal: number, activeZone: string }} Render metadata.
	 */
	const renderPanelPricing = ( panel, data ) => {

		return renderPricing( {
			annualData: data.annualData,
			monthlyData: data.monthlyData,
			elements: getPanelRenderElements( panel ),
			term: state.term,
			templates,
			i18n,
		} );

	};

	/**
	 * Ensures requester for one panel mode.
	 *
	 * @param {string} modeKey Mode key.
	 * @return {Function|null} Request function.
	 */
	const ensurePanelRequester = ( modeKey ) => {

		if ( requestersByMode.has( modeKey ) ) {

			return requestersByMode.get( modeKey );

		}

		const panel = getPanelByMode( modeKey );

		if ( ! panel ) {

			return null;

		}

		let pendingSignature = '';

		const requester = createPricingRequester( {
			apiUrl,
			/**
			 * Returns current request params for active panel.
			 *
			 * @return {{term: string, coreSeats: number, ultimateSeats: number, extendedSeats: number}} Params payload.
			 */
			getParams: () => {

				const seats = getSeatsForPanel( panel );
				pendingSignature = getSeatSignature( seats );

				return {
					term: state.term,
					...seats,
				};

			},
			/**
			 *
			 */
			onStart: () => {

				if ( state.planMode === modeKey ) {

					showLoading();

				}

			},
			/**
			 * Handles successful pricing response.
			 *
			 * @param {{ annualData: Object, monthlyData: Object }} root0 Pricing pair for annual and monthly terms.
			 */
			onSuccess: ( { annualData, monthlyData } ) => {

				cacheByMode.set( modeKey, {
					seatSignature: pendingSignature,
					annualData,
					monthlyData,
				} );

				if ( state.planMode === modeKey ) {

					const renderMeta = renderPanelPricing( panel, { annualData, monthlyData } );
					const seats = getSeatsForPanel( panel );
					const previousZone = state.activeCtaZone;

					if (
						renderMeta.activeZone &&
						previousZone &&
						previousZone !== renderMeta.activeZone
					) {

						trackAmplitudeCtaZoneShown(
							block,
							renderMeta.activeZone,
							state.planMode,
							renderMeta.subtotal,
							state.term,
							seats
						);

					}

					state.activeCtaZone = renderMeta.activeZone;
					hideLoading();

				}

			},
			/**
			 * Handles pricing request failure.
			 *
			 * @param {Error|unknown} error Request error.
			 */
			onError: ( error ) => {

				if ( state.planMode === modeKey ) {

					console.error( 'Pricing Calculator API error:', error );
					renderPricingError( {
						elements: getPanelRenderElements( panel ),
						i18n,
					} );
					hideLoading();

				}

			},
		} );

		requestersByMode.set( modeKey, requester );

		return requester;

	};

	/**
	 * Renders active panel from cache or requests fresh data.
	 *
	 * @param {string} modeKey Mode key.
	 * @param {boolean} forceRefresh Whether request should bypass cache.
	 * @return {void}
	 */
	const renderOrRequestPanel = ( modeKey, forceRefresh = false ) => {

		const panel = getPanelByMode( modeKey );

		if ( ! panel ) {

			return;

		}

		const seats = getSeatsForPanel( panel );
		const signature = getSeatSignature( seats );
		const cached = forceRefresh ? null : getCachedPricing( modeKey, signature );

		if ( cached ) {

			const renderMeta = renderPanelPricing( panel, cached );
			const seats = getSeatsForPanel( panel );
			const previousZone = state.activeCtaZone;

			if (
				renderMeta.activeZone &&
				previousZone &&
				previousZone !== renderMeta.activeZone
			) {

				trackAmplitudeCtaZoneShown(
					block,
					renderMeta.activeZone,
					state.planMode,
					renderMeta.subtotal,
					state.term,
					seats
				);

			}

			state.activeCtaZone = renderMeta.activeZone;
			return;

		}

		const requester = ensurePanelRequester( modeKey );
		requester?.();

	};

	/**
	 * Prefetches all tab pricing once on load.
	 *
	 * @return {void}
	 */
	const prefetchAllPanels = () => {

		controls.panels.forEach( ( panel ) => {

			const modeKey = panel.dataset.mode;

			if ( modeKey ) {

				renderOrRequestPanel( modeKey, true );

			}

		} );

	};

	bindBillingToggle( {
		buttons: controls.billingButtons,
		/**
		 * Handles billing term toggle.
		 *
		 * @param {string} nextTerm Selected billing term.
		 */
		onChange: ( nextTerm ) => {

			trackAmplitudeBillingCycleToggle( block, state.term, nextTerm, state.planMode );
			state.term = nextTerm;
			renderOrRequestPanel( state.planMode, false );

		},
	} );

	bindTabs( {
		tabs: controls.tabs,
		panels: controls.panels,
		/**
		 * Handles plan tab change.
		 *
		 * @param {string} nextPlanMode Selected plan mode key.
		 */
		onChange: ( nextPlanMode ) => {

			trackAmplitudeTabSwitch( block, state.planMode, nextPlanMode );
			state.planMode = nextPlanMode;
			renderOrRequestPanel( state.planMode, false );

		},
	} );

	const debouncedActiveRefresh = debounce( () => {

		const activePanel = getPanelByMode( state.planMode );
		const seats = getSeatsForPanel( activePanel );

		trackAmplitudeSeatsAdjusted( block, state.planMode, seats );
		renderOrRequestPanel( state.planMode, true );

	}, 300 );

	controls.planGroups.forEach( ( planGroup ) => {

		bindSliderPair( {
			slider: planGroup.querySelector( '[data-role="seat-range"]' ),
			input: planGroup.querySelector( '[data-role="seat-input"]' ),
			onChange: debouncedActiveRefresh,
		} );

	} );

	prefetchAllPanels();

};

document.querySelectorAll( '.b-pricing-calculator' ).forEach( initPricingCalculator );
